import {
  SelectOption,
  useModalContext,
  WSButton,
  WSElement,
  WSElementProps,
  WSForm,
  WSSelect
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useState } from "react";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  useIntegrationsQuickbooks,
  useIntegrationsQuickbooksCustomers,
  useIntegrationsQuickbooksItems
} from "../../../query/integrations/queries";
import { getSortedAndGroupedCustomerList } from "../../../query/integrations/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { getIntegrationUserSelectLabel } from "../../../Settings/screens/Integrations/quickbooks/QuickbooksSetupStep6";
import {
  INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT,
  QUICKBOOKS_ENTITY,
  RefreshModal
} from "../../../Settings/screens/Integrations/quickbooks/RefreshButton";
import { useFormContext } from "react-hook-form";

type Props = WSElementProps;

export const NewClientIntegrations: React.FC<Props> = () => {
  const qFeatureFlags = useFeatureFlags();

  const qIntegrationsQuickbooks = useIntegrationsQuickbooks();
  const qIntegrationsQuickbooksCustomers = useIntegrationsQuickbooksCustomers();
  const qIntegrationsQuickbooksItems = useIntegrationsQuickbooksItems();

  const { openModal, closeModal } = useModalContext();
  const [showIntegrations, setShowIntegrations] = useState(false);

  const { setValue } = useFormContext();

  useEffect(() => {
    if (!showIntegrations) {
      setValue("client.qboCustomerId", "");
      setValue("client.qboItemId", "");
    }
  }, [showIntegrations, setValue]);

  return (
    <WSQueries
      queries={{
        qFeatureFlags,
        qIntegrationsQuickbooks,
        qIntegrationsQuickbooksCustomers,
        qIntegrationsQuickbooksItems
      }}
      renderErrors={() => null}
    >
      {({
        qIntegrationsQuickbooks: { data: integrationsQuickbooks },
        qIntegrationsQuickbooksCustomers: {
          data: integrationsQuickbooksCustomers
        },
        qIntegrationsQuickbooksItems: { data: integrationsQuickbooksItems }
      }) => {
        const customers = getSortedAndGroupedCustomerList(
          integrationsQuickbooksCustomers
        );

        const quickbooksCustomerOptions: SelectOption[] = customers.map(
          qboCustomer => {
            return {
              label: getIntegrationUserSelectLabel(qboCustomer),
              value: qboCustomer.customerId,
              searchText: [
                qboCustomer.primaryEmailAddress,
                qboCustomer.displayName,
                qboCustomer.companyName,
                qboCustomer.givenName,
                qboCustomer.familyName
              ]
                .filter(Boolean)
                .join(" ")
            };
          }
        );

        const quickbooksItemsOptions: SelectOption[] = integrationsQuickbooksItems
          .map(qboItem => {
            return {
              label: qboItem.fullyQualifiedName,
              value: qboItem.itemId,
              searchText: [qboItem.fullyQualifiedName, qboItem.name]
                .filter(Boolean)
                .join(" ")
            };
          })
          .sort((a, b) => a.label.toLocaleLowerCase().localeCompare(b.label));

        const defaultItem = integrationsQuickbooksItems.find(
          item => item.itemId === integrationsQuickbooks.defaults?.itemId
        );

        return (
          <>
            <RefreshModal />
            <WSButton.Link
              mt="S"
              type="button"
              name="integrationsAction"
              icon={showIntegrations ? "circleMinus" : "circlePlus"}
              onClick={() => setShowIntegrations(v => !v)}
            >
              {showIntegrations ? "Remove integrations" : "Add integrations"}
            </WSButton.Link>

            <WSElement
              style={{ display: showIntegrations ? "block" : "none" }}
              mt="XS"
            >
              <WSForm.Field
                mb="M"
                name="client.qboCustomerId"
                component={WSSelect}
                componentProps={{
                  placeholder: "Not mapped, create new Customer",
                  options: quickbooksCustomerOptions,
                  searchable: true,
                  cleanable: true,
                  placeholderActions: [
                    {
                      label: "Resync QBO Customers",
                      icon: "sync",
                      callback() {
                        openModal(INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT, {
                          entity: QUICKBOOKS_ENTITY.CUSTOMERS
                        });
                      }
                    }
                  ]
                }}
                label="QBO Customer"
              />
              <WSForm.Field
                mb="L"
                name="client.qboItemId"
                component={WSSelect}
                componentProps={{
                  options: quickbooksItemsOptions,
                  placeholder: `Use default "${defaultItem?.fullyQualifiedName}"`,
                  cleanable: true,
                  searchable: true,
                  placeholderActions: [
                    {
                      label: "Resync QBO Items",
                      icon: "sync",
                      callback() {
                        openModal(INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT, {
                          entity: QUICKBOOKS_ENTITY.ITEMS
                        });
                      }
                    }
                  ]
                }}
                label="Default QBO Item"
              />
            </WSElement>
            <RefreshModal />
          </>
        );
      }}
    </WSQueries>
  );
};
